import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Search } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { apiClient } from "../lib/api";
import { DEPARTMENTS } from "../components/Footer";

const H = { fontFamily: "Quicksand, sans-serif" };
const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const TopCommunes = ({ list }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dept-top-communes">
    {list.map((c) => (
      <Link key={c.slug} to={`/villes/${c.slug}`} className="group relative aspect-[4/3] rounded-3xl overflow-hidden bg-secondary" data-testid={`dept-top-${c.slug}`}>
        <img src={c.image} alt={`Conciergerie Airbnb à ${c.name}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/85 to-transparent" />
        <div className="absolute bottom-0 p-4 text-white">
          <div className="font-bold text-lg leading-tight" style={H}>{c.name}</div>
          <div className="text-xs text-white/80 mt-1">{c.occupancy_rate}% occup. · {c.adr} €/nuit</div>
        </div>
      </Link>
    ))}
  </div>
);

const AllCommunes = ({ list, name }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => list.filter((c) => norm(c.name).includes(norm(q))), [list, q]);
  return (
    <div data-testid="dept-all-communes">
      <div className="relative max-w-md mt-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Chercher une commune (${list.length})`}
          className="w-full pl-11 pr-4 py-3 rounded-full border border-border bg-white focus:outline-none focus:border-accent" data-testid="dept-commune-search" />
      </div>
      <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2 text-sm">
        {shown.map((c) => (
          <li key={c.slug}>
            <Link to={`/villes/${c.slug}`} className="hover:text-accent transition-colors" data-testid={`dept-commune-${c.slug}`} title={`Conciergerie Airbnb ${c.name}`}>{c.name}</Link>
          </li>
        ))}
      </ul>
      {shown.length === 0 && <p className="mt-6 text-muted-foreground" data-testid="dept-commune-empty">Aucune commune trouvée {name}.</p>}
    </div>
  );
};

const OtherDepts = ({ current }) => (
  <div className="flex flex-wrap gap-2" data-testid="dept-others">
    {DEPARTMENTS.filter(([s]) => s !== current).map(([s, n]) => (
      <Link key={s} to={`/departements/${s}`} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-secondary hover:bg-accent/10 transition-colors" data-testid={`dept-other-${s}`}>
        <MapPin size={12} className="text-accent" /> {n}
      </Link>
    ))}
  </div>
);

export default function DepartmentPage() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setD(null); setErr(false);
    apiClient.get(`/departements/${slug}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <PublicLayout><div className="container-tm py-32 text-center"><h1 className="h-section">Département introuvable</h1><Link to="/villes" className="text-accent mt-4 inline-block">← Toutes les communes</Link></div></PublicLayout>;
  if (!d) return <PublicLayout><div className="container-tm py-32 text-center text-muted-foreground">Chargement…</div></PublicLayout>;

  return (
    <PublicLayout>
      <SEO title={d.seo_title} description={d.seo_description} image={d.communes[0]?.image} />
      <section className="pt-8 pb-12" data-testid="dept-hero">
        <div className="container-tm">
          <nav className="text-sm text-foreground/80 mb-6 flex flex-wrap gap-1.5" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-accent">Accueil</Link> · <Link to="/villes" className="hover:text-accent">Nos communes</Link> · <span className="font-bold text-primary">{d.name}</span>
          </nav>
          <span className="pill">{d.name} · {d.code}</span>
          <h1 className="h-display mt-5 max-w-4xl" data-testid="dept-title">Conciergerie Airbnb <span className="text-accent">{d.en.replace(/-/g, "\u2011")}</span></h1>
          <p className="mt-5 text-muted-foreground text-lg max-w-3xl">{d.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <Link to="/devis" className="btn-accent" data-testid="dept-cta-devis">Demander mon devis gratuit <ArrowRight size={16} /></Link>
            <div className="flex gap-6 text-sm" data-testid="dept-stats">
              <div><div className="text-2xl font-bold text-primary" style={H}>{d.count}</div>communes</div>
              <div><div className="text-2xl font-bold text-primary" style={H}>{d.occupancy_rate}%</div>occupation moy.</div>
              <div><div className="text-2xl font-bold text-primary" style={H}>{d.adr} €</div>prix moyen / nuit</div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-10"><div className="container-tm">
        <h2 className="h-section mb-8">Les communes les plus demandées {d.en}</h2>
        <TopCommunes list={d.communes.slice(0, 12)} />
      </div></section>
      <section className="py-10"><div className="container-tm">
        <h2 className="h-section">Toutes les communes {d.en}</h2>
        <AllCommunes list={d.communes} name={d.en} />
      </div></section>
      <section className="py-10"><div className="container-tm max-w-4xl space-y-5" data-testid="dept-faq">
        <h2 className="h-section mb-4">Questions fréquentes</h2>
        {d.faq.map((f, i) => (
          <div key={f.q} className="border-b border-border pb-5" data-testid={`dept-faq-${i}`}>
            <h3 className="font-bold text-lg" style={H}>{f.q}</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div></section>
      <section className="py-10"><div className="container-tm">
        <h2 className="h-section mb-6">Autres départements</h2>
        <OtherDepts current={d.slug} />
      </div></section>
    </PublicLayout>
  );
}
